<template>
  <div class="container-fluid">
    <!-- ------------------------ -->
    <!-- OVERLAY: race panel      -->
    <!-- ------------------------ -->
    <RaceSidePanel
      v-if="isSidePanelOpen"
      :race="race"
      :raceNames="raceNames"
    />
    <!-- ------------------------ -->
    <!-- NOTIFICATIONS            -->
    <!-- ------------------------ -->
    <notifications
      width="500"
      group="bb-confirm"
      :speed="100"
      :duration="1000"
      position="top right"
      classes="vue-notification"
    />
    <!-- ------------------- -->
    <!-- MAIN ROW            -->
    <!-- ------------------- -->
    <div class="row">
      <!-- ------------------- -->
      <!-- COL1: buttons panel -->
      <!-- ------------------- -->
      <div class="col-md-1 rounded">
        <RaceButtonsPanel />
      </div>
      <!-- --------------------- -->
      <!-- COL2: race table -->
      <!-- --------------------- -->
      <div class="col-md-9">
        <TableRaces
          v-if="races && loadReady"
          :key="raceTableKey"
          :headings="headings"
          :races="races"
        />
      </div>
    </div>
    <div
      v-if="!loadReady"
      class="bb-loader"
    ></div>
  </div>
</template>

<script>
//========================================================
// scripts
//---------
import { inject } from 'vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import TableRaces from '../components/TableRaces.vue'
import RaceSidePanel from '../components/RaceSidePanel.vue'
import RaceButtonsPanel from '../components/RaceButtonsPanel.vue'

import useEditRaceCharacteristic from '../composables/useEditRaceCharacteristic'
import useDeleteRace from '../composables/useDeleteRace'
import useEditRace from '../composables/useEditRace'

import createRaceCharacteristicEntries from '../helpers/createRaceCharacteristicEntries'
import loadRaceCharacteristicValues from '../helpers/loadRaceCharacteristicValues'
import unloadCharacteristics from '../helpers/unloadCharacteristics'
import loadRaceCharacteristicProps from '../helpers/loadRaceCharacteristicProps'
import showNotification from '../helpers/showNotification'
import getRaceByIndex from '../helpers/getRaceByIndex'
import getPrevious from '../helpers/getPrevious'
import handleError from '../helpers/handleError'
import getNext from '../helpers/getNext'

export default {
  name: 'RacesView',
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // components
  //------------
  components: {
    TableRaces,
    RaceSidePanel,
    RaceButtonsPanel,
  },
  //========================================================
  // data
  //------
  data() {
    return {
      endpoint: '',
      local_slug: '',
      deleteInd: false,
      isSidePanelOpen: false,
      loadReady: false,
      arena: null,
      race: null,
      config: null,
      races: [],
      errors: [],
      errorFields: [],
      name: 'RaceList',
      headings: [
        {
          id: 1,
          label: '',
          field: 'image',
          sortable: false,
          tdClass: 'text-center',
        },
        {
          id: 2,
          label: 'Name',
          field: 'name',
          tdClass: 'text-center',
          formatFn: (value) => {
            return (value || '').replace(/\b\w/g, (char) => char.toUpperCase())
          },
        },
        {
          id: 3,
          label: 'CA',
          field: 'ca',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 4,
          label: 'SR',
          field: 'sr',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 5,
          label: 'Move',
          field: 'move',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 6,
          label: 'Dam Mod',
          field: this.hideZeros,
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 7,
          label: 'STR',
          field: 'str',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 8,
          label: 'CON',
          field: 'con',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 9,
          label: 'DEX',
          field: 'dex',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 10,
          label: 'SIZ',
          field: 'siz',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 11,
          label: 'INT',
          field: 'int',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 12,
          label: 'POW',
          field: 'pow',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 13,
          label: 'CHA',
          field: 'cha',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 14,
          label: 'Special Action',
          field: 'special_action_image',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 15,
          label: 'Special Reaction',
          field: 'special_reaction_image',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 16,
          label: 'Character Race',
          field: 'character_race_image',
          tdClass: 'text-center',
          sortable: false,
        },
        {
          id: 17,
          label: 'Delete',
          field: 'action_delete',
          tdClass: 'text-center',
          width: '100px',
          sortable: false,
        },
        {
          id: 18,
          label: 'Special Action',
          field: 'special_action',
          tdClass: 'text-center',
          sortable: false,
          hidden: true,
        },
        {
          id: 19,
          label: 'Special Reaction',
          field: 'special_reaction',
          tdClass: 'text-center',
          sortable: false,
          hidden: true,
        },
        {
          id: 20,
          label: 'Character Race',
          field: 'character_race',
          tdClass: 'text-center',
          sortable: false,
          hidden: true,
        },
        {
          id: 21,
          label: 'Name',
          field: 'name',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 22,
          label: 'Id',
          field: 'id',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 23,
          label: 'Slug',
          field: 'slug',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 24,
          label: 'Combatant Count',
          field: 'combatant_count',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 25,
          label: 'Character Count',
          field: 'character_count',
          tdClass: 'text-center',
          hidden: true,
        },
      ],
    }
  },
  //========================================================
  // computed
  //----------
  computed: {
    raceTableKey() {
      return this.races.map((race) => race.id).join('|')
    },
    raceNames() {
      const names_array = this.races.map(function (array) {
        return array.name
      })
      return names_array
    },
    isInvertedSidePanelOpen() {
      return !this.isSidePanelOpen
    },
    api_request() {
      return window.bbAPIRequest(this.endpoint, this.local_slug, this.deleteInd)
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    //--------------------
    // ref mapped methods
    //--------------------
    ...mapMutations('auth', {
      setCurrentView: 'setCurrentView',
      setMessage: 'setMessage',
      destroyToken: 'destroyToken',
      destroyUserProfile: 'destroyUserProfile',
    }),
    ...mapGetters('combat', {
      getCampaign: 'getCampaign',
      getConfig: 'getConfig',
      getCampaignPlayers: 'getCampaignPlayers',
    }),
    ...mapGetters('races', {
      getRaces: 'getRaces',
    }),
    ...mapActions('combat', {
      loadConfig: 'loadConfig',
    }),
    ...mapActions('races', {
      loadStaticRaces: 'loadStaticRaces',
      loadStaticCharacteristics: 'loadStaticCharacteristics',
    }),
    //========================================================
    // hide zeros
    //------------
    hideZeros(rowObj) {
      if (rowObj.damage_mod == '0') {
        return ''
      } else {
        return rowObj.damage_mod
      }
    },
    //========================================================
    // exit view
    //-----------
    exitView() {
      this.emitter.off('add-race')
      this.emitter.off('update-race')
      this.emitter.off('delete-race')
      this.emitter.off('show-race-panel')
      this.emitter.off('exit-race-view')

      this.setView('')
      this.$router.go(-1)
    },
    //========================================================
    // get campaign handler
    //-------------------
    getCampaignHandler() {
      this.arena = this.getCampaign()
    },
    //========================================================
    // show race panel
    //-----------------
    showRacePanel() {
      this.race = null
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
    //========================================================
    // set view
    //----------
    setView(view) {
      this.setCurrentView(view)
    },
    //========================================================
    // add race harness
    //------------------
    async addRaceHarness(new_race) {
      // console.log("-------------------------------")
      // console.log("IN addRaceHarness");
      //---------------------------------------------
      // copy characteristics to update chars struct
      //---------------------------------------------
      let char_entries = createRaceCharacteristicEntries(new_race)
      //-------------------------------------------------
      // strip-out characteristics from main race record
      //-------------------------------------------------
      new_race = unloadCharacteristics(new_race)
      //----------
      // add race
      //----------
      let call = useEditRace(new_race)
      await call
        .then((race) => {
          //---------------------------------
          // add new race id to char entries
          //---------------------------------
          char_entries.map((e) => (e.payload.race = race.id))
          //-----------------------
          // add race char records
          //-----------------------
          this.updateRaceCharacteristicsHarness(
            char_entries,
            new_race.name,
            'added.',
          )
        })
        .catch((err) => {
          handleError(err, 38)
        })
    },
    //========================================================
    // update race harness
    //---------------------
    async updateRaceHarness(updated_race, chars_changed) {
      // console.log("-------------------------------");
      // console.log("IN updateRaceHarness");
      //---------------------------------------------
      // copy characteristics to update chars struct
      //---------------------------------------------
      let char_entries = createRaceCharacteristicEntries(updated_race)
      //-------------------------------------------------
      // strip-out characteristics from main race record
      //-------------------------------------------------
      updated_race = unloadCharacteristics(updated_race)
      //-------------
      // update race
      //-------------
      let call = useEditRace(updated_race)
      await call
        .then(() => {
          //----------------------------------------
          // update race char records where changed
          //----------------------------------------
          if (chars_changed) {
            this.updateRaceCharacteristicsHarness(
              char_entries,
              updated_race.name,
              'updated.',
            )
            //-------------
            // else reload
            //-------------
          } else {
            //--------------
            // reload races
            //--------------
            this.loadRacesHandler()
            //--------------
            // show message
            //--------------
            showNotification('Race ' + updated_race.name + ' updated.')
          }
        })
        .catch((err) => {
          handleError(err, 47)
        })
    },
    //========================================================
    // update race characteristics harness
    //-------------------------------------
    async updateRaceCharacteristicsHarness(
      char_entries,
      race_name,
      action_type,
    ) {
      // console.log("-------------------------------");
      // console.log("IN updateRaceCharacteristicsHarness");
      let promises = []
      //------------------------
      // loop thru char entries
      //------------------------
      char_entries.forEach((e) => {
        //-------------------------
        // update main race record
        //-------------------------
        let call = useEditRaceCharacteristic(e)
          .then((response) => {
            return response
          })
          .catch((err) => {
            handleError(err, 48)
          })
        //---------------
        // store promise
        //---------------
        promises.push(call)
      })
      //--------------
      // all finished
      //--------------
      Promise.all(promises).then(() => {
        //--------------
        // reload races
        //--------------
        this.loadRacesHandler()
        //--------------
        // show message
        //--------------
        showNotification('Race ' + race_name + ' ' + action_type)
      })
    },
    //========================================================
    // delete race harness
    //---------------------
    async deleteRaceHarness(delete_index) {
      // console.log("----------------");
      // console.log("IN deleteRaceHarness idx = " + delete_index);
      //----------
      // get race
      //----------
      const race = getRaceByIndex(delete_index)
      //------------------
      // call delete race
      //------------------
      let call = useDeleteRace(race.slug)
      await call
        .then(() => {
          let msg = ''
          if (race.race) {
            msg = 'Race & related Race ' + race.name + ' deleted.'
          } else {
            msg = 'Race ' + race.name + ' deleted.'
          }
          this.loadRacesHandler()
          showNotification(msg)
        })
        .catch((err) => {
          handleError(err, 40)
        })
    },
    //========================================================
    // load config handler
    //---------------------
    async loadConfigHandler() {
      //----------
      // api call
      //----------
      await this.loadConfig()
        //------------
        // successful
        //------------
        .then(() => {
          this.config = this.getConfig()
        })
        //-------
        // error
        //-------
        .catch((err) => {
          handleError(err, 41)
        })
    },
    //========================================================
    // load races handler
    //--------------------
    async loadRacesHandler() {
      this.loadReady = false
      //---------------
      // make API call
      //---------------
      let api_call = this.loadStaticRaces(true)
      //---------------
      // await results
      //---------------
      await api_call
        .then(() => {
          //------------------
          // get stored races
          //------------------
          this.races = this.getRaces()
          //---------------------------
          // load characteristic props
          //---------------------------
          this.races = loadRaceCharacteristicProps(this.races)
          //----------------------------
          // load characteristic values
          //----------------------------
          this.races = loadRaceCharacteristicValues(this.races)
          //-----------------------------
          // refresh selected race object
          //-----------------------------
          if (this.race?.id != null) {
            this.race =
              this.races.find((race) => race.id === this.race.id) ?? this.race
          }
          //
          this.loadReady = true
        })
        .catch((err) => {
          handleError(err, 42)
        })
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    //--------------------
    // load races handler
    //--------------------
    this.loadRacesHandler()
    //-----------------------------
    // load static characteristics
    //-----------------------------
    this.loadStaticCharacteristics()
    //-------------------
    // get campaign handler
    //-------------------
    this.getCampaignHandler()
    //---------------
    // set race view
    //---------------
    this.setView('races')
    //========================================================
    // show race panel handler
    //-------------------------
    this.emitter.on('show-race-panel', () => {
      this.showRacePanel()
    })
    //========================================================
    // sidepanel detect emit
    //-----------------------
    this.emitter.on('toggle-sidepanel-race', (race) => {
      this.race = race
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    })
    //========================================================
    // exit view handler
    //-------------------
    this.emitter.on('exit-race-view', () => {
      this.loadRacesHandler()
      this.exitView()
    })
    //========================================================
    // next race emit
    //----------------
    this.emitter.on('next-race', (race) => {
      this.race = getNext(race.id, this.races)
      this.emitter.emit('load-race', this.race)
    })
    //========================================================
    // previous race emit
    //--------------------
    this.emitter.on('previous-race', (race) => {
      this.race = getPrevious(race.id, this.races)
      this.emitter.emit('load-race', this.race)
    })
    //========================================================
    // close side panel
    //------------------
    this.emitter.on('close-race-side-panel', (ind) => {
      this.isSidePanelOpen = ind
    })
    //========================================================
    // add race
    //----------
    this.emitter.on('add-race', (parms) => {
      //-------------------------
      // perform race call (add)
      //-------------------------
      this.addRaceHarness(parms.race)
    })
    //========================================================
    // update race
    //-------------
    this.emitter.on('update-race', (parms) => {
      //----------------------------
      // perform race call (update)
      //----------------------------
      this.updateRaceHarness(parms.race, parms.chars_changed)
    })
    //========================================================
    // delete race
    //-------------
    this.emitter.on('delete-race', (delete_index) => {
      //---------------------------------
      // perform race call (delete)
      //---------------------------------
      this.deleteRaceHarness(delete_index)
    })
  },
}
</script>
